import { useEffect, useMemo, useState } from "react";
import { VestigingFilter } from "../components/VestigingFilter";
import { useVestigingKeuze, type VestigingKeuze } from "../components/useVestigingKeuze";
import {
  CatalogusLijst, SoortTegels, WeergaveKeuze, soortTegelsUitRijen,
  type Weergave,
} from "../components/CatalogusLijst";
import { leesWeergave, bewaarWeergave } from "../components/CatalogusKop";
import { speciesIconSrc } from "../components/AnimalOverview";
import { AiHulpKnop, AiTariefHulp, useAiBeschikbaar, type TariefSeed } from "../components/AiTariefHulp";
import { BtwKeuze, btwWeergave, huidigeBtwUitHistorie, useBtwCodes } from "../components/BtwKeuze";
import { Baby } from "lucide-react";
import { OverviewHeader } from "../components/OverviewHeader";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import {
  TrashIcon,
  PencilIcon,
  Button,
  CheckboxField,
  Field,
  Modal,
  SelectField,
  StatusBadge,
  TextareaField,
  type Column,
  type StatusTone,
  PlusIcon,
  RotateRightIcon,
} from "@fawkes/ui";

import { formatDate, formatEuro, todayIso } from "../i18n/format";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

// tarief-types Epic 4 (Story 4.1–4.4): the Crèche tarief overview — the React port of the Jmix
// `CrecheTariefOverviewView`. Three top tabs (Losse dagen / Rittenkaarten / Abonnementen):
//   • Losse dagen — per-category dagprijs (+ dagprijs incl. overnachting) grid; add opens
//     NieuwCrecheTariefDialog (the CrecheTariefkopDialog port); edit opens TariefBewerkDialog (the same
//     4-section editor as pension: Basisinformatie / Huidig tarief / Prijswijziging inplannen /
//     Tariefoverzicht).
//   • Rittenkaarten — punch/ride card grid (aantalRitten / prijs / geldigheidMaanden / overdraagbaar /
//     verlengen); create/edit dialog + soft-delete.
//   • Abonnementen — subscription grid (dagenPerWeek / prijsPerMaand / strategy / looptijd / status badge
//     ACTIEF/CONCEPT); create/edit dialog + a publiceren toggle (CONCEPT ↔ ACTIEF) + soft-delete.
//
// Pakketsaldo (Story 4.4) is a read-only per-customer card (recalc out of scope) — surfaced as an
// optional "pakketsaldo opzoeken" lookup panel at the bottom of the Abonnementen tab rather than as a
// primary tab, matching the Jmix layout where it is a customer-card and not a tariff tab.
//
// All endpoints use RELATIVE urls (the dataProvider prepends /api once; a leading slash doubles to
// /api/api). ROLE_TARIEF_EDIT is server-enforced — there is no client signal, so affordances stay
// enabled and a 403 surfaces inline. A back-dated prijswijziging needs ROLE_TARIEF_ADMIN server-side
// → its 403 surfaces with a distinct "you need admin rights for a past date" message.

// ── enum vocabularies (stable wire ids, matching the Java enums) ──────────────────────────────────
const EXTRA_DAGEN_STRATEGIES = ["RITTENKAART", "LOSSE_DAGPRIJS"] as const;
const LOOPTIJD_TYPES = ["DOORLOPEND", "BEPAALDE_TIJD"] as const;

// ── wire DTOs ──────────────────────────────────────────────────────────────────────────────────────
interface CrecheTariefRow {
  tariefId: number;
  version: number | null;
  vestigingId: number | null;
  diersoortId: number | null;
  diersoortNaam: string;
  categorienaam: string;
  omschrijving: string | null;
  dagprijs: number | null;
  dagprijsInclOvernachting: number | null;
  geldigVanaf: string | null;
  laatstGewijzigd: string | null;
  isNieuw: boolean;
}

interface TariefHistorieRegel {
  versieId: number;
  bedrag: number;
  bedragHoog: number | null;
  effectdatum: string;
  einddatum: string | null;
  reden: string | null;
  btwCodeId: number | null;
  status: "GEPLAND" | "ACTIEF" | "VERVALLEN";
}

interface Rittenkaart {
  id: number;
  version: number;
  naam: string;
  vestigingId: number | null;
  diersoortId: number | null;
  aantalRitten: number;
  prijs: number;
  geldigheidMaanden: number | null;
  overdraagbaar: boolean;
  stuckRuleVerlengen: boolean;
  actief: boolean;
}

interface Abonnement {
  id: number;
  version: number;
  naam: string;
  vestigingId: number | null;
  diersoortId: number | null;
  dagenPerWeek: number;
  vasteDagenJson: string | null;
  prijsPerMaand: number;
  extraDagenStrategy: string | null;
  looptijdType: string | null;
  looptijdMaanden: number | null;
  status: string | null;
  actief: boolean;
}

interface Pakketsaldo {
  saldoId: number;
  dierNaam: string;
  diersoortNaam: string | null;
  rittenkaartNaam: string | null;
  rittenOver: number | null;
  rittenTotaal: number | null;
  verloopDatum: string | null;
  abonnementNaam: string | null;
  abonnementActief: boolean;
  dagenPerWeek: number | null;
}

// ── helpers ──────────────────────────────────────────────────────────────────────────────────────
type SaveNotice = "conflict" | "forbidden" | "forbiddenPast" | "error" | null;
const statusToErr = (err: unknown, isPast = false): SaveNotice => {
  const code = (err as { statusCode?: number }).statusCode;
  if (code === 409) return "conflict";
  if (code === 403) return isPast ? "forbiddenPast" : "forbidden";
  return "error";
};
const num = (v: string): number | null => {
  if (v.trim() === "") return null;
  const n = Number(v.replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

function historieTone(status: TariefHistorieRegel["status"]): StatusTone {
  if (status === "ACTIEF") return "success";
  if (status === "GEPLAND") return "warning";
  return "neutral";
}

// ── the page ─────────────────────────────────────────────────────────────────────────────────────
type Tab = "losseDagen" | "rittenkaarten" | "abonnementen";

export function CrecheTariefOverview() {
  const t = useTranslate();
  const [tab, setTab] = useState<Tab>("losseDagen");
  // Het bereik waarover je kijkt: alles, alleen wat organisatiebreed geldt, of alleen wat een
  // vestiging zelf heeft ingesteld. Client-side, want de rijen dragen hun vestiging al mee.
  // Dezelfde vestigingkeuze als op Producten & diensten en het factuuroverzicht.
  const vestiging = useVestigingKeuze();

  const tabs: { key: Tab; label: string }[] = [
    { key: "losseDagen", label: t("crecheTarief.tab.losseDagen") },
    { key: "rittenkaarten", label: t("crecheTarief.tab.rittenkaarten") },
    { key: "abonnementen", label: t("crecheTarief.tab.abonnementen") },
  ];

  return (
    <div className="product-overview pension-tarief">
      <OverviewHeader
        icon={<Baby size={24} />}
        title={t("menu.crecheTarief")}
      />
      <div className="product-tabs" role="tablist" aria-label={t("crecheTarief.tabs.label")}>
        {tabs.map((tabDef) => (
          <button
            key={tabDef.key}
            type="button"
            role="tab"
            aria-selected={tab === tabDef.key}
            className={`product-tab${tab === tabDef.key ? " product-tab--active" : ""}`}
            onClick={() => setTab(tabDef.key)}
          >
            {tabDef.label}
          </button>
        ))}
      </div>

      {tab === "losseDagen" && <LosseDagenTab vestiging={vestiging} />}
      {tab === "rittenkaarten" && <RittenkaartenTab hoortErbij={vestiging.hoortErbij} />}
      {tab === "abonnementen" && <AbonnementenTab hoortErbij={vestiging.hoortErbij} />}
    </div>
  );
}

// ── Losse dagen tab ──────────────────────────────────────────────────────────────────────────────
function LosseDagenTab({ vestiging }: { vestiging: VestigingKeuze }) {
  const { hoortErbij } = vestiging;
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";

  // Source parity: the "Geldig op" peildatum filter (empty = today, the api default).
  const [peildatum, setPeildatum] = useState("");
  const { data, isLoading, isError, refetch } = useCustom<CrecheTariefRow[]>({
    url: peildatum ? `creche-tarief?peildatum=${encodeURIComponent(peildatum)}` : "creche-tarief",
    method: "get",
  });
  const binnenVestiging = useMemo(
    () => (Array.isArray(data?.data) ? data!.data : []).filter((r) => hoortErbij(r)),
    [data, hoortErbij],
  );

  // Waar je op dit scherm tussen kiest is de diersoort. De tegels tellen binnen de vestigingkeuze
  // maar vóór de soortkeuze: anders springt elke andere tegel op nul zodra je er één aanklikt.
  const [diersoort, setDiersoort] = useState("");
  const tegels = useMemo(() => soortTegelsUitRijen(
    binnenVestiging,
    (r) => r.diersoortNaam,
    t("dierAanwezig.stat.total"),
    (soort) => <img src={speciesIconSrc(soort)} alt="" width={28} height={28} />,
  ), [binnenVestiging, t]);
  const rows = useMemo(
    () => (diersoort ? binnenVestiging.filter((r) => r.diersoortNaam === diersoort) : binnenVestiging),
    [binnenVestiging, diersoort],
  );
  const [weergave, setWeergave] = useState<Weergave>(() => leesWeergave("fawkes.crecheTarief"));
  const kiesWeergave = (w: Weergave) => {
    setWeergave(w);
    bewaarWeergave("fawkes.crecheTarief", w);
  };

  // ── client search / sort / paging (Jmix grid behaviour) ──
  const [zoek, setZoek] = useState("");
  const [sortKey, setSortKey] = useState<
    "categorienaam" | "diersoortNaam" | "dagprijs" | "dagprijsInclOvernachting"
  >("categorienaam");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState(0);
  const pageSize = 10;

  const filtered = useMemo(() => {
    const q = zoek.trim().toLowerCase();
    const matched = q
      ? rows.filter(
          (r) =>
            r.categorienaam.toLowerCase().includes(q) ||
            (r.diersoortNaam ?? "").toLowerCase().includes(q) ||
            (r.omschrijving ?? "").toLowerCase().includes(q),
        )
      : rows;
    const sorted = [...matched].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      let cmp: number;
      if (typeof av === "number" || typeof bv === "number") {
        cmp = (av == null ? -Infinity : (av as number)) - (bv == null ? -Infinity : (bv as number));
      } else {
        cmp = String(av ?? "").localeCompare(String(bv ?? ""));
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
    return sorted;
  }, [rows, zoek, sortKey, sortDir]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = filtered.slice(page * pageSize, page * pageSize + pageSize);
  useEffect(() => {
    if (page > pageCount - 1) setPage(0);
  }, [page, pageCount]);

  const [nieuwOpen, setNieuwOpen] = useState(false);
  const [nieuwSeed, setNieuwSeed] = useState<TariefSeed | undefined>(undefined);
  const [aiOpen, setAiOpen] = useState(false);
  const aiBeschikbaar = useAiBeschikbaar("creche-tarief/ai-suggestie");
  const [bewerkRow, setBewerkRow] = useState<CrecheTariefRow | null>(null);
  const { mutate: del } = useCustomMutation();
  const [rowNotice, setRowNotice] = useState<SaveNotice>(null);

  const onDelete = (r: CrecheTariefRow) => {
    setRowNotice(null);
    if (!globalThis.confirm?.(t("crecheTarief.delete.confirm", { args: [r.categorienaam] }))) return;
    del(
      {
        url: `creche-tarief/${r.tariefId}`,
        method: "delete",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => refetch(),
        onError: (err) => setRowNotice(statusToErr(err)),
      },
    );
  };

  const columns: Column<CrecheTariefRow>[] = [
    {
      header: t("crecheTarief.col.categorie"),
      cell: (r) => (
        <span className="pension-tarief__nieuw-cell">
          {r.categorienaam}
          {r.isNieuw && <StatusBadge tone="success">{t("crecheTarief.badge.nieuw")}</StatusBadge>}
        </span>
      ),
    },
    {
      header: t("crecheTarief.col.diersoort"),
      // Als badge, zoals de productenlijst zijn categoriekolommen toont.
      cell: (r) => (r.diersoortNaam ? <StatusBadge tone="neutral">{r.diersoortNaam}</StatusBadge> : "—"),
    },
    {
      header: t("crecheTarief.col.dagprijs"),
      align: "right",
      cell: (r) => (r.dagprijs != null ? formatEuro(r.dagprijs, locale) : t("crecheTarief.none")),
    },
    {
      header: t("crecheTarief.col.dagprijsOvernachting"),
      align: "right",
      cell: (r) =>
        r.dagprijsInclOvernachting != null ? (
          formatEuro(r.dagprijsInclOvernachting, locale)
        ) : (
          <span className="pension-tarief__missing">{t("crecheTarief.dagprijsOvernachting.missing")}</span>
        ),
    },
    {
      header: t("crecheTarief.col.geldigVanaf"),
      cell: (r) => (r.geldigVanaf ? formatDate(r.geldigVanaf, locale) : t("crecheTarief.none")),
    },
    {
      header: t("crecheTarief.col.acties"),
      cell: (r) => (
        <span className="asiel-tarief__row-actions">
          <button
            type="button"
            className="tarief-rijactie"
            onClick={() => setBewerkRow(r)}
            title={t("common.edit")}
            aria-label={t("common.edit")}
          >
            <PencilIcon width={16} height={16} />
          </button>
          <button
            type="button"
            className="tarief-rijactie"
            onClick={() => onDelete(r)}
            title={t("crecheTarief.action.delete")}
            aria-label={t("crecheTarief.action.delete")}
          >
            <TrashIcon width={16} height={16} />
          </button>
        </span>
      ),
    },
  ];

  return (
    <div className="asiel-tarief__panel">
      <div className="asiel-tarief__toolbar">
        <VestigingFilter
          label={t("financieel.filter.vestiging")}
          opties={vestiging.opties}
          gekozen={vestiging.gekozen}
          onChange={vestiging.setGekozen}
        />
        <Field
          label={t("crecheTarief.search")}
          placeholder={t("crecheTarief.search")}
          value={zoek}
          onChange={(e) => {
            setZoek(e.target.value);
            setPage(0);
          }}
        />
        <Field
          label={t("crecheTarief.peildatum")}
          type="date"
          value={peildatum}
          onChange={(e) => {
            setPeildatum(e.target.value);
            setPage(0);
          }}
        />
        <SelectField
          label={t("crecheTarief.sort.label")}
          value={sortKey}
          onChange={(e) => {
            setSortKey(e.target.value as typeof sortKey);
            setPage(0);
          }}
          options={[
            { value: "categorienaam", label: t("crecheTarief.col.categorie") },
            { value: "diersoortNaam", label: t("crecheTarief.col.diersoort") },
            { value: "dagprijs", label: t("crecheTarief.col.dagprijs") },
            { value: "dagprijsInclOvernachting", label: t("crecheTarief.col.dagprijsOvernachting") },
          ]}
        />
        <SelectField
          label={t("crecheTarief.sort.dir")}
          value={sortDir}
          onChange={(e) => {
            setSortDir(e.target.value as "asc" | "desc");
            setPage(0);
          }}
          options={[
            { value: "asc", label: t("crecheTarief.sort.asc") },
            { value: "desc", label: t("crecheTarief.sort.desc") },
          ]}
        />
        <div className="asiel-tarief__toolbar-knoppen">
          {aiBeschikbaar && <AiHulpKnop onClick={() => setAiOpen(true)} />}
          <button type="button" className="fawkes-products-vernieuw" onClick={() => refetch()}>
            <RotateRightIcon width={16} height={16} />
            {t("crecheTarief.action.refresh")}
          </button>
          <Button onClick={() => { setNieuwSeed(undefined); setNieuwOpen(true); }}>
            <PlusIcon width={16} height={16} /> {t("crecheTarief.action.add")}
          </Button>
        </div>
      </div>

      {rowNotice && (
        <div className="asiel-tarief__notice asiel-tarief__notice--error" role="alert">
          {t(`crecheTarief.notice.${rowNotice}`)}
        </div>
      )}

      {isError ? (
        <div className="asiel-tarief__notice asiel-tarief__notice--error">{t("crecheTarief.load.error")}</div>
      ) : (
        <>
          {tegels.length > 0 && (
            <SoortTegels
              label={t("crecheTarief.col.diersoort")}
              gekozen={diersoort}
              onKies={(k) => { setDiersoort(k); setPage(0); }}
              tegels={tegels}
            />
          )}
          <div className="fawkes-catalogus__lijstkop">
            <span className="fawkes-muted">
              {filtered.length}{" "}{t(filtered.length === 1 ? "dierAanwezig.results.foundOne" : "dierAanwezig.results.found")}
            </span>
            <WeergaveKeuze
              weergave={weergave}
              onKies={kiesWeergave}
              labels={{
                groep: t("catalogus.weergave"),
                kaart: t("catalogus.weergave.kaart"),
                compact: t("catalogus.weergave.compact"),
                lijst: t("catalogus.weergave.lijst"),
              }}
            />
          </div>
          <CatalogusLijst
            columns={columns}
            rows={pageRows}
            getRowKey={(r) => String(r.tariefId)}
            empty={isLoading ? t("common.loading") : t("crecheTarief.empty")}
            weergave={weergave}
          />
          {pageCount > 1 && (
            <div className="pension-tarief__paging">
              <Button
                variant="secondary"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                {t("crecheTarief.paging.prev")}
              </Button>
              <span className="pension-tarief__paging-info">
                {t("crecheTarief.paging.info", { args: [String(page + 1), String(pageCount)] })}
              </span>
              <Button
                variant="secondary"
                disabled={page >= pageCount - 1}
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              >
                {t("crecheTarief.paging.next")}
              </Button>
            </div>
          )}
        </>
      )}

      {aiOpen && (
        <AiTariefHulp
          pad="creche-tarief/ai-suggestie"
          voorbeeld={t("tariefAi.voorbeeld.creche")}
          vandaag={todayIso()}
          onClose={() => setAiOpen(false)}
          onNieuw={(seed) => { setAiOpen(false); setNieuwSeed(seed); setNieuwOpen(true); }}
          onBewerk={(tariefId) => {
            const rij = binnenVestiging.find((r) => String(r.tariefId) === tariefId);
            if (rij) { setAiOpen(false); setBewerkRow(rij); }
          }}
          onVerwijder={(tariefId) => {
            const rij = binnenVestiging.find((r) => String(r.tariefId) === tariefId);
            if (rij) { setAiOpen(false); onDelete(rij); }
          }}
        />
      )}
      {nieuwOpen && (
        <NieuwCrecheTariefDialog
          seed={nieuwSeed}
          onClose={() => setNieuwOpen(false)}
          onSaved={() => {
            setNieuwOpen(false);
            refetch();
          }}
        />
      )}

      {bewerkRow && (
        <TariefBewerkDialog
          row={bewerkRow}
          onClose={() => setBewerkRow(null)}
          onSaved={() => {
            setBewerkRow(null);
            refetch();
          }}
        />
      )}
    </div>
  );
}

// ── NieuwCrecheTariefDialog (create head + first version; the CrecheTariefkopDialog port) ──────────
function NieuwCrecheTariefDialog({ onClose, onSaved, seed }: {
  onClose: () => void;
  onSaved: () => void;
  /** Voorgevulde velden, bijvoorbeeld uit de AI-hulp. Alles blijft daarna gewoon te wijzigen. */
  seed?: TariefSeed;
}) {
  const t = useTranslate();
  const [categorienaam, setCategorienaam] = useState(seed?.naam ?? "");
  const [diersoortId, setDiersoortId] = useState(seed?.diersoortId ?? "");
  const [omschrijving, setOmschrijving] = useState("");
  const [dagprijs, setDagprijs] = useState(seed?.bedrag ?? "");
  const [dagprijsOvernachting, setDagprijsOvernachting] = useState(seed?.bedragHoog ?? "");
  const [geldigVanaf, setGeldigVanaf] = useState(seed?.effectdatum ?? todayIso());
  // Een nieuw tarief heeft geen eerdere versie om BTW van over te nemen: hier moet hij gekozen worden.
  const btwCodes = useBtwCodes();
  const [btwCodeId, setBtwCodeId] = useState("");

  const [naamMissing, setNaamMissing] = useState(false);
  const [bedragMissing, setBedragMissing] = useState(false);
  const [btwMissing, setBtwMissing] = useState(false);
  const [notice, setNotice] = useState<SaveNotice>(null);
  const { mutate, isLoading: saving } = useCustomMutation();

  const submit = () => {
    const prijs = num(dagprijs);
    setNaamMissing(categorienaam.trim() === "");
    setBedragMissing(prijs == null);
    setBtwMissing(btwCodeId === "");
    if (categorienaam.trim() === "" || prijs == null || btwCodeId === "") return;
    setNotice(null);

    const isPast = geldigVanaf !== "" && geldigVanaf < todayIso();
    mutate(
      {
        url: "creche-tarief",
        method: "post",
        values: {
          categorienaam: categorienaam.trim(),
          diersoortId: diersoortId === "" ? null : Number(diersoortId),
          omschrijving: omschrijving.trim() === "" ? null : omschrijving.trim(),
          dagprijs: prijs,
          dagprijsInclOvernachting: num(dagprijsOvernachting),
          geldigVanaf: geldigVanaf === "" ? null : geldigVanaf,
          btwCodeId: Number(btwCodeId),
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => onSaved(),
        onError: (err) => setNotice(statusToErr(err, isPast)),
      },
    );
  };

  return (
    <Modal
      open
      title={t("crecheTarief.nieuw.title")}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button onClick={submit} disabled={saving}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <div className="asiel-tarief__form">
        {notice && (
          <div className="asiel-tarief__notice asiel-tarief__notice--error" role="alert">
            {t(`crecheTarief.notice.${notice}`)}
          </div>
        )}
        <Field
          label={t("crecheTarief.field.categorie")}
          value={categorienaam}
          onChange={(e) => setCategorienaam(e.target.value)}
        />
        {naamMissing && (
          <div className="asiel-tarief__field-error">{t("crecheTarief.field.categorie.required")}</div>
        )}
        <Field
          label={t("crecheTarief.field.diersoortId")}
          value={diersoortId}
          inputMode="numeric"
          onChange={(e) => setDiersoortId(e.target.value)}
        />
        <TextareaField
          label={t("crecheTarief.field.omschrijving")}
          value={omschrijving}
          rows={2}
          onChange={(e) => setOmschrijving(e.target.value)}
        />
        <Field
          label={t("crecheTarief.field.dagprijs")}
          value={dagprijs}
          inputMode="decimal"
          onChange={(e) => setDagprijs(e.target.value)}
        />
        {bedragMissing && (
          <div className="asiel-tarief__field-error">{t("crecheTarief.field.dagprijs.required")}</div>
        )}
        <Field
          label={t("crecheTarief.field.dagprijsOvernachting")}
          value={dagprijsOvernachting}
          inputMode="decimal"
          onChange={(e) => setDagprijsOvernachting(e.target.value)}
        />
        <Field
          label={t("crecheTarief.field.geldigVanaf")}
          type="date"
          value={geldigVanaf}
          onChange={(e) => setGeldigVanaf(e.target.value)}
        />
        <BtwKeuze
          value={btwCodeId}
          onChange={(v) => { setBtwCodeId(v); setBtwMissing(false); }}
          codes={btwCodes}
          missing={btwMissing}
          t={t}
        />
      </div>
    </Modal>
  );
}

// ── TariefBewerkDialog (the 4-section full editor; mirrors the pension editor) ─────────────────────
// Section 1 Basisinformatie — categorienaam/diersoort/omschrijving editable, vestiging read-only
//   → PUT /{id}/basisinfo
// Section 2 Huidig tarief — the active dagprijs + dagprijs-incl-overnachting (read-only)
// Section 3 Prijswijziging inplannen — one or more future versions → POST /{id}/prijswijziging
//   (bedrag = dagprijs; bedragHoog = dagprijs incl. overnachting)
// Section 4 Tariefoverzicht — version history (GET /{id}/historie) with status badges
interface GeplandeRow {
  effectdatum: string;
  bedrag: string;
  bedragHoog: string;
  reden: string;
  /** Verplicht; voorgevuld met de BTW van de lopende versie, want meestal wijzigt alleen het bedrag. */
  btwCodeId: string;
}

function TariefBewerkDialog({
  row,
  onClose,
  onSaved,
}: {
  row: CrecheTariefRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";

  // ── Section 1: Basisinformatie ──
  const [categorienaam, setCategorienaam] = useState(row.categorienaam);
  const [diersoortId, setDiersoortId] = useState(row.diersoortId != null ? String(row.diersoortId) : "");
  const [omschrijving, setOmschrijving] = useState(row.omschrijving ?? "");
  const [basisNotice, setBasisNotice] = useState<SaveNotice>(null);
  const [basisSaved, setBasisSaved] = useState(false);
  const { mutate: saveBasis, isLoading: savingBasis } = useCustomMutation();

  const submitBasis = () => {
    if (categorienaam.trim() === "") {
      setBasisNotice("error");
      return;
    }
    setBasisNotice(null);
    setBasisSaved(false);
    saveBasis(
      {
        url: `creche-tarief/${row.tariefId}/basisinfo`,
        method: "put",
        values: {
          categorienaam: categorienaam.trim(),
          diersoortId: diersoortId === "" ? null : Number(diersoortId),
          omschrijving: omschrijving.trim() === "" ? null : omschrijving.trim(),
          version: row.version,
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          setBasisSaved(true);
          onSaved();
        },
        onError: (err) => setBasisNotice(statusToErr(err)),
      },
    );
  };

  // ── Section 4: Tariefoverzicht (history) ──
  const {
    data: histData,
    isLoading: histLoading,
    refetch: refetchHist,
  } = useCustom<TariefHistorieRegel[]>({
    url: `creche-tarief/${row.tariefId}/historie`,
    method: "get",
  });
  const historie = useMemo(() => (Array.isArray(histData?.data) ? histData!.data : []), [histData]);
  const btwCodes = useBtwCodes();
  const huidigeBtw = huidigeBtwUitHistorie(historie);

  // ── Section 3: Prijswijziging inplannen ──
  const [geplande, setGeplande] = useState<GeplandeRow[]>([]);
  const [prijsNotice, setPrijsNotice] = useState<SaveNotice>(null);
  const [btwMissing, setBtwMissing] = useState(false);
  const { mutate: planWijziging, isLoading: planning } = useCustomMutation();

  const addRow = () =>
    setGeplande((g) => [
      ...g,
      { effectdatum: todayIso(), bedrag: "", bedragHoog: "", reden: "", btwCodeId: huidigeBtw },
    ]);
  const removeRow = (idx: number) => setGeplande((g) => g.filter((_, i) => i !== idx));
  const setRow = (idx: number, patch: Partial<GeplandeRow>) =>
    setGeplande((g) => g.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  // Een rij die al openstond voordat de historie binnen was, krijgt de lopende BTW alsnog — maar een
  // rij waar de gebruiker zelf iets koos, laten we staan.
  useEffect(() => {
    if (huidigeBtw === "") return;
    setGeplande((g) => g.map((r) => (r.btwCodeId === "" ? { ...r, btwCodeId: huidigeBtw } : r)));
  }, [huidigeBtw]);

  const submitPrijswijziging = () => {
    const valid = geplande.filter((r) => r.effectdatum !== "" && num(r.bedrag) != null);
    if (valid.length === 0) {
      setPrijsNotice("error");
      return;
    }
    // BTW is verplicht per versie; niet versturen zolang er een rij zonder BTW tussen zit.
    if (valid.some((r) => r.btwCodeId === "")) {
      setBtwMissing(true);
      return;
    }
    setBtwMissing(false);
    setPrijsNotice(null);
    const anyPast = valid.some((r) => r.effectdatum < todayIso());

    const payload =
      valid.length === 1
        ? {
            bedrag: num(valid[0].bedrag),
            bedragHoog: num(valid[0].bedragHoog),
            effectdatum: valid[0].effectdatum,
            reden: valid[0].reden.trim() === "" ? null : valid[0].reden.trim(),
            btwCodeId: Number(valid[0].btwCodeId),
          }
        : {
            geplandeWijzigingen: valid.map((r) => ({
              effectdatum: r.effectdatum,
              bedrag: num(r.bedrag),
              bedragHoog: num(r.bedragHoog),
              btwCodeId: Number(r.btwCodeId),
              reden: r.reden.trim() === "" ? null : r.reden.trim(),
            })),
          };

    planWijziging(
      {
        url: `creche-tarief/${row.tariefId}/prijswijziging`,
        method: "post",
        values: payload,
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          setGeplande([]);
          refetchHist();
          onSaved();
        },
        onError: (err) => setPrijsNotice(statusToErr(err, anyPast)),
      },
    );
  };

  const histColumns: Column<TariefHistorieRegel>[] = [
    { header: t("crecheTarief.hist.vanaf"), cell: (r) => formatDate(r.effectdatum, locale) },
    { header: t("crecheTarief.hist.tot"), cell: (r) => formatDate(r.einddatum, locale) },
    { header: t("crecheTarief.hist.bedrag"), align: "right", cell: (r) => formatEuro(r.bedrag, locale) },
    {
      header: t("crecheTarief.hist.bedragHoog"),
      align: "right",
      cell: (r) => (r.bedragHoog != null ? formatEuro(r.bedragHoog, locale) : "—"),
    },
    { header: t("btw.kolom"), align: "right", cell: (r) => btwWeergave(btwCodes, r.btwCodeId) },
    { header: t("crecheTarief.hist.reden"), cell: (r) => r.reden ?? "—" },
    {
      header: t("crecheTarief.hist.status"),
      cell: (r) => (
        <StatusBadge tone={historieTone(r.status)}>{t(`crecheTarief.hist.status.${r.status}`)}</StatusBadge>
      ),
    },
  ];

  return (
    <Modal
      open
      size="wide"
      title={t("crecheTarief.bewerk.title", { args: [row.categorienaam] })}
      onClose={onClose}
      footer={
        <Button variant="secondary" onClick={onClose}>
          {t("common.close")}
        </Button>
      }
    >
      <div className="asiel-tarief__form">
        {/* ── Section 1: Basisinformatie ── */}
        <fieldset className="asiel-tarief__section">
          <legend>{t("crecheTarief.section.basis")}</legend>
          {basisNotice && (
            <div className="asiel-tarief__notice asiel-tarief__notice--error" role="alert">
              {t(`crecheTarief.notice.${basisNotice}`)}
            </div>
          )}
          {basisSaved && (
            <div className="asiel-tarief__notice asiel-tarief__notice--success" role="status">
              {t("crecheTarief.basis.saved")}
            </div>
          )}
          <Field
            label={t("crecheTarief.field.categorie")}
            value={categorienaam}
            onChange={(e) => setCategorienaam(e.target.value)}
          />
          <Field
            label={t("crecheTarief.field.diersoortId")}
            value={diersoortId}
            inputMode="numeric"
            onChange={(e) => setDiersoortId(e.target.value)}
          />
          <TextareaField
            label={t("crecheTarief.field.omschrijving")}
            value={omschrijving}
            rows={2}
            onChange={(e) => setOmschrijving(e.target.value)}
          />
          <Field
            label={t("crecheTarief.field.vestiging")}
            value={row.vestigingId != null ? String(row.vestigingId) : t("crecheTarief.vestiging.org")}
            readOnly
            disabled
          />
          <div>
            <Button onClick={submitBasis} disabled={savingBasis}>
              {t("crecheTarief.basis.save")}
            </Button>
          </div>
        </fieldset>

        {/* ── Section 2: Huidig tarief ── */}
        <fieldset className="asiel-tarief__section">
          <legend>{t("crecheTarief.section.huidig")}</legend>
          <div className="pension-tarief__current">
            <div>
              <span className="pension-tarief__current-label">{t("crecheTarief.col.dagprijs")}</span>
              <span className="pension-tarief__current-value">
                {row.dagprijs != null ? formatEuro(row.dagprijs, locale) : t("crecheTarief.none")}
              </span>
            </div>
            <div>
              <span className="pension-tarief__current-label">
                {t("crecheTarief.col.dagprijsOvernachting")}
              </span>
              <span className="pension-tarief__current-value">
                {row.dagprijsInclOvernachting != null
                  ? formatEuro(row.dagprijsInclOvernachting, locale)
                  : t("crecheTarief.dagprijsOvernachting.missing")}
              </span>
            </div>
            <div>
              <span className="pension-tarief__current-label">{t("crecheTarief.col.geldigVanaf")}</span>
              <span className="pension-tarief__current-value">
                {row.geldigVanaf ? formatDate(row.geldigVanaf, locale) : t("crecheTarief.none")}
              </span>
            </div>
          </div>
        </fieldset>

        {/* ── Section 3: Prijswijziging inplannen ── */}
        <fieldset className="asiel-tarief__section">
          <legend>{t("crecheTarief.section.prijswijziging")}</legend>
          {prijsNotice && (
            <div className="asiel-tarief__notice asiel-tarief__notice--error" role="alert">
              {t(`crecheTarief.notice.${prijsNotice}`)}
            </div>
          )}
          <p className="asiel-tarief__cond-empty">{t("crecheTarief.prijswijziging.hint")}</p>
          {btwMissing && (
            <div className="asiel-tarief__field-error" role="alert">{t("btw.verplicht")}</div>
          )}
          {geplande.map((r, idx) => (
            <div className="pension-tarief__plan-row pension-tarief__plan-row--btw" key={idx} data-testid="plan-row">
              <Field
                label={t("crecheTarief.field.effectdatum")}
                type="date"
                value={r.effectdatum}
                onChange={(e) => setRow(idx, { effectdatum: e.target.value })}
              />
              <Field
                label={t("crecheTarief.field.dagprijs")}
                value={r.bedrag}
                inputMode="decimal"
                onChange={(e) => setRow(idx, { bedrag: e.target.value })}
              />
              <Field
                label={t("crecheTarief.field.dagprijsOvernachting")}
                value={r.bedragHoog}
                inputMode="decimal"
                onChange={(e) => setRow(idx, { bedragHoog: e.target.value })}
              />
              <BtwKeuze
                value={r.btwCodeId}
                onChange={(v) => { setRow(idx, { btwCodeId: v }); setBtwMissing(false); }}
                codes={btwCodes}
                missing={false}
                t={t}
              />
              <Field
                label={t("crecheTarief.field.reden")}
                value={r.reden}
                onChange={(e) => setRow(idx, { reden: e.target.value })}
              />
              <button
                type="button"
                className="asiel-tarief__link asiel-tarief__cond-remove"
                onClick={() => removeRow(idx)}
                aria-label={t("crecheTarief.prijswijziging.remove")}
              >
                ×
              </button>
            </div>
          ))}
          <div className="asiel-tarief__row-actions">
            <Button variant="secondary" onClick={addRow}>
              {t("crecheTarief.prijswijziging.add")}
            </Button>
            {geplande.length > 0 && (
              <Button onClick={submitPrijswijziging} disabled={planning}>
                {t("crecheTarief.prijswijziging.submit")}
              </Button>
            )}
          </div>
        </fieldset>

        {/* ── Section 4: Tariefoverzicht (history) ── */}
        <fieldset className="asiel-tarief__section">
          <legend>{t("crecheTarief.section.overzicht")}</legend>
          <IndeelbareTabel tabelKey="creche-tarief-overview-1"
            columns={histColumns}
            rows={historie}
            getRowKey={(r) => String(r.versieId)}
            empty={histLoading ? t("common.loading") : t("crecheTarief.hist.empty")}
          />
        </fieldset>
      </div>
    </Modal>
  );
}

// ── Rittenkaarten tab ────────────────────────────────────────────────────────────────────────────
function RittenkaartenTab({ hoortErbij }: { hoortErbij: (rij: { vestigingId?: number | null }) => boolean }) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";

  const { data, isLoading, isError, refetch } = useCustom<Rittenkaart[]>({
    url: "creche-tarief/rittenkaarten",
    method: "get",
  });
  const rows = useMemo(
    () => (Array.isArray(data?.data) ? data!.data : []).filter((r) => hoortErbij(r)),
    [data, hoortErbij],
  );

  const [dialog, setDialog] = useState<Rittenkaart | "new" | null>(null);
  const { mutate: del } = useCustomMutation();
  const [rowNotice, setRowNotice] = useState<SaveNotice>(null);

  const onDelete = (r: Rittenkaart) => {
    setRowNotice(null);
    if (!globalThis.confirm?.(t("crecheTarief.rittenkaart.delete.confirm", { args: [r.naam] }))) return;
    del(
      {
        url: `creche-tarief/rittenkaarten/${r.id}`,
        method: "delete",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => refetch(),
        onError: (err) => setRowNotice(statusToErr(err)),
      },
    );
  };

  const jaNee = (v: boolean) => (v ? t("crecheTarief.yes") : t("crecheTarief.no"));

  const columns: Column<Rittenkaart>[] = [
    { header: t("crecheTarief.rittenkaart.col.naam"), cell: (r) => r.naam },
    { header: t("crecheTarief.rittenkaart.col.aantalRitten"), align: "right", cell: (r) => r.aantalRitten },
    {
      header: t("crecheTarief.rittenkaart.col.prijs"),
      align: "right",
      cell: (r) => formatEuro(r.prijs, locale),
    },
    {
      header: t("crecheTarief.rittenkaart.col.geldigheidMaanden"),
      align: "right",
      cell: (r) => (r.geldigheidMaanden != null ? r.geldigheidMaanden : t("crecheTarief.none")),
    },
    { header: t("crecheTarief.rittenkaart.col.overdraagbaar"), cell: (r) => jaNee(r.overdraagbaar) },
    { header: t("crecheTarief.rittenkaart.col.verlengen"), cell: (r) => jaNee(r.stuckRuleVerlengen) },
    {
      header: t("crecheTarief.col.acties"),
      cell: (r) => (
        <span className="asiel-tarief__row-actions">
          <button
            type="button"
            className="tarief-rijactie"
            onClick={() => setDialog(r)}
            title={t("common.edit")}
            aria-label={t("common.edit")}
          >
            <PencilIcon width={16} height={16} />
          </button>
          <button
            type="button"
            className="tarief-rijactie"
            onClick={() => onDelete(r)}
            title={t("crecheTarief.action.delete")}
            aria-label={t("crecheTarief.action.delete")}
          >
            <TrashIcon width={16} height={16} />
          </button>
        </span>
      ),
    },
  ];

  return (
    <div className="asiel-tarief__panel">
      <div className="asiel-tarief__toolbar">
        <Button onClick={() => setDialog("new")}>
          <PlusIcon width={16} height={16} /> {t("crecheTarief.rittenkaart.add")}
        </Button>
        <span className="asiel-tarief__toolbar-spacer" />
        <Button variant="secondary" onClick={() => refetch()}>
          {t("crecheTarief.action.refresh")}
        </Button>
      </div>

      {rowNotice && (
        <div className="asiel-tarief__notice asiel-tarief__notice--error" role="alert">
          {t(`crecheTarief.notice.${rowNotice}`)}
        </div>
      )}

      {isError ? (
        <div className="asiel-tarief__notice asiel-tarief__notice--error">
          {t("crecheTarief.rittenkaart.load.error")}
        </div>
      ) : (
        <IndeelbareTabel tabelKey="creche-tarief-overview-2"
          columns={columns}
          rows={rows}
          getRowKey={(r) => String(r.id)}
          empty={isLoading ? t("common.loading") : t("crecheTarief.rittenkaart.empty")}
        />
      )}

      {dialog !== null && (
        <RittenkaartDialog
          card={dialog === "new" ? null : dialog}
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null);
            refetch();
          }}
        />
      )}
    </div>
  );
}

function RittenkaartDialog({
  card,
  onClose,
  onSaved,
}: {
  card: Rittenkaart | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useTranslate();

  const [naam, setNaam] = useState(card?.naam ?? "");
  const [diersoortId, setDiersoortId] = useState(card?.diersoortId != null ? String(card.diersoortId) : "");
  const [aantalRitten, setAantalRitten] = useState(card?.aantalRitten != null ? String(card.aantalRitten) : "");
  const [prijs, setPrijs] = useState(card?.prijs != null ? String(card.prijs) : "");
  const [geldigheidMaanden, setGeldigheidMaanden] = useState(
    card?.geldigheidMaanden != null ? String(card.geldigheidMaanden) : "",
  );
  const [overdraagbaar, setOverdraagbaar] = useState(card?.overdraagbaar ?? false);
  const [verlengen, setVerlengen] = useState(card?.stuckRuleVerlengen ?? false);

  const [naamMissing, setNaamMissing] = useState(false);
  const [aantalMissing, setAantalMissing] = useState(false);
  const [prijsMissing, setPrijsMissing] = useState(false);
  const [notice, setNotice] = useState<SaveNotice>(null);
  const { mutate, isLoading: saving } = useCustomMutation();

  const submit = () => {
    const aantal = num(aantalRitten);
    const prijsN = num(prijs);
    setNaamMissing(naam.trim() === "");
    setAantalMissing(aantal == null);
    setPrijsMissing(prijsN == null);
    if (naam.trim() === "" || aantal == null || prijsN == null) return;
    setNotice(null);

    const values = {
      naam: naam.trim(),
      vestigingId: null,
      diersoortId: diersoortId === "" ? null : Number(diersoortId),
      aantalRitten: aantal,
      prijs: prijsN,
      geldigheidMaanden: num(geldigheidMaanden),
      overdraagbaar,
      stuckRuleVerlengen: verlengen,
      actief: true,
      ...(card ? { version: card.version } : {}),
    };

    mutate(
      {
        url: card ? `creche-tarief/rittenkaarten/${card.id}` : "creche-tarief/rittenkaarten",
        method: card ? "put" : "post",
        values,
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => onSaved(),
        onError: (err) => setNotice(statusToErr(err)),
      },
    );
  };

  return (
    <Modal
      open
      title={card ? t("crecheTarief.rittenkaart.dialog.edit.title") : t("crecheTarief.rittenkaart.dialog.new.title")}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button onClick={submit} disabled={saving}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <div className="asiel-tarief__form">
        {notice && (
          <div className="asiel-tarief__notice asiel-tarief__notice--error" role="alert">
            {t(`crecheTarief.notice.${notice}`)}
          </div>
        )}
        <Field
          label={t("crecheTarief.rittenkaart.field.naam")}
          value={naam}
          onChange={(e) => setNaam(e.target.value)}
        />
        {naamMissing && (
          <div className="asiel-tarief__field-error">{t("crecheTarief.rittenkaart.field.naam.required")}</div>
        )}
        <Field
          label={t("crecheTarief.field.diersoortId")}
          value={diersoortId}
          inputMode="numeric"
          onChange={(e) => setDiersoortId(e.target.value)}
        />
        <Field
          label={t("crecheTarief.rittenkaart.field.aantalRitten")}
          value={aantalRitten}
          inputMode="numeric"
          onChange={(e) => setAantalRitten(e.target.value)}
        />
        {aantalMissing && (
          <div className="asiel-tarief__field-error">
            {t("crecheTarief.rittenkaart.field.aantalRitten.required")}
          </div>
        )}
        <Field
          label={t("crecheTarief.rittenkaart.field.prijs")}
          value={prijs}
          inputMode="decimal"
          onChange={(e) => setPrijs(e.target.value)}
        />
        {prijsMissing && (
          <div className="asiel-tarief__field-error">{t("crecheTarief.rittenkaart.field.prijs.required")}</div>
        )}
        <Field
          label={t("crecheTarief.rittenkaart.field.geldigheidMaanden")}
          value={geldigheidMaanden}
          inputMode="numeric"
          onChange={(e) => setGeldigheidMaanden(e.target.value)}
        />
        <CheckboxField
          label={t("crecheTarief.rittenkaart.field.overdraagbaar")}
          checked={overdraagbaar}
          onChange={(e) => setOverdraagbaar(e.target.checked)}
        />
        <CheckboxField
          label={t("crecheTarief.rittenkaart.field.verlengen")}
          checked={verlengen}
          onChange={(e) => setVerlengen(e.target.checked)}
        />
      </div>
    </Modal>
  );
}

// ── Abonnementen tab ─────────────────────────────────────────────────────────────────────────────
function AbonnementenTab({ hoortErbij }: { hoortErbij: (rij: { vestigingId?: number | null }) => boolean }) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";

  const { data, isLoading, isError, refetch } = useCustom<Abonnement[]>({
    url: "creche-tarief/abonnementen",
    method: "get",
  });
  const rows = useMemo(
    () => (Array.isArray(data?.data) ? data!.data : []).filter((r) => hoortErbij(r)),
    [data, hoortErbij],
  );

  const [dialog, setDialog] = useState<Abonnement | "new" | null>(null);
  const { mutate: del } = useCustomMutation();
  const { mutate: publiceer } = useCustomMutation();
  const [rowNotice, setRowNotice] = useState<SaveNotice>(null);

  const onDelete = (r: Abonnement) => {
    setRowNotice(null);
    if (!globalThis.confirm?.(t("crecheTarief.abonnement.delete.confirm", { args: [r.naam] }))) return;
    del(
      {
        url: `creche-tarief/abonnementen/${r.id}`,
        method: "delete",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => refetch(),
        onError: (err) => setRowNotice(statusToErr(err)),
      },
    );
  };

  const onPubliceer = (r: Abonnement) => {
    setRowNotice(null);
    publiceer(
      {
        url: `creche-tarief/abonnementen/${r.id}/publiceren`,
        method: "post",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => refetch(),
        onError: (err) => setRowNotice(statusToErr(err)),
      },
    );
  };

  const columns: Column<Abonnement>[] = [
    { header: t("crecheTarief.abonnement.col.naam"), cell: (r) => r.naam },
    { header: t("crecheTarief.abonnement.col.dagenPerWeek"), align: "right", cell: (r) => r.dagenPerWeek },
    {
      header: t("crecheTarief.abonnement.col.prijsPerMaand"),
      align: "right",
      cell: (r) => formatEuro(r.prijsPerMaand, locale),
    },
    {
      header: t("crecheTarief.abonnement.col.strategy"),
      cell: (r) =>
        r.extraDagenStrategy ? t(`crecheTarief.abonnement.strategy.${r.extraDagenStrategy}`) : "—",
    },
    {
      header: t("crecheTarief.abonnement.col.looptijd"),
      cell: (r) => (r.looptijdType ? t(`crecheTarief.abonnement.looptijd.${r.looptijdType}`) : "—"),
    },
    {
      header: t("crecheTarief.abonnement.col.status"),
      cell: (r) => (
        <StatusBadge tone={r.status === "ACTIEF" ? "success" : "warning"}>
          {r.status ? t(`crecheTarief.abonnement.status.${r.status}`) : "—"}
        </StatusBadge>
      ),
    },
    {
      header: t("crecheTarief.col.acties"),
      cell: (r) => (
        <span className="asiel-tarief__row-actions">
          <button
            type="button"
            className="tarief-rijactie"
            onClick={() => setDialog(r)}
            title={t("common.edit")}
            aria-label={t("common.edit")}
          >
            <PencilIcon width={16} height={16} />
          </button>
          <button type="button" className="asiel-tarief__link" onClick={() => onPubliceer(r)}>
            {r.status === "ACTIEF"
              ? t("crecheTarief.abonnement.action.depubliceren")
              : t("crecheTarief.abonnement.action.publiceren")}
          </button>
          <button
            type="button"
            className="tarief-rijactie"
            onClick={() => onDelete(r)}
            title={t("crecheTarief.action.delete")}
            aria-label={t("crecheTarief.action.delete")}
          >
            <TrashIcon width={16} height={16} />
          </button>
        </span>
      ),
    },
  ];

  return (
    <div className="asiel-tarief__panel">
      <div className="asiel-tarief__toolbar">
        <Button onClick={() => setDialog("new")}>{t("crecheTarief.abonnement.add")}</Button>
        <span className="asiel-tarief__toolbar-spacer" />
        <Button variant="secondary" onClick={() => refetch()}>
          {t("crecheTarief.action.refresh")}
        </Button>
      </div>

      {rowNotice && (
        <div className="asiel-tarief__notice asiel-tarief__notice--error" role="alert">
          {t(`crecheTarief.notice.${rowNotice}`)}
        </div>
      )}

      {isError ? (
        <div className="asiel-tarief__notice asiel-tarief__notice--error">
          {t("crecheTarief.abonnement.load.error")}
        </div>
      ) : (
        <IndeelbareTabel tabelKey="creche-tarief-overview-3"
          columns={columns}
          rows={rows}
          getRowKey={(r) => String(r.id)}
          empty={isLoading ? t("common.loading") : t("crecheTarief.abonnement.empty")}
        />
      )}

      {dialog !== null && (
        <AbonnementDialog
          abonnement={dialog === "new" ? null : dialog}
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null);
            refetch();
          }}
        />
      )}

      {/* Pakketsaldo (Story 4.4): a read-only per-customer lookup (recalc out of scope). Surfaced as a
          small panel here rather than as a primary tab — it is a customer-card, not a tariff list. */}
      <PakketsaldoPanel />
    </div>
  );
}

function AbonnementDialog({
  abonnement,
  onClose,
  onSaved,
}: {
  abonnement: Abonnement | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useTranslate();

  const [naam, setNaam] = useState(abonnement?.naam ?? "");
  const [diersoortId, setDiersoortId] = useState(
    abonnement?.diersoortId != null ? String(abonnement.diersoortId) : "",
  );
  const [dagenPerWeek, setDagenPerWeek] = useState(
    abonnement?.dagenPerWeek != null ? String(abonnement.dagenPerWeek) : "",
  );
  const [prijsPerMaand, setPrijsPerMaand] = useState(
    abonnement?.prijsPerMaand != null ? String(abonnement.prijsPerMaand) : "",
  );
  const [extraDagenStrategy, setExtraDagenStrategy] = useState(
    abonnement?.extraDagenStrategy ?? "RITTENKAART",
  );
  const [looptijdType, setLooptijdType] = useState(abonnement?.looptijdType ?? "DOORLOPEND");
  const [looptijdMaanden, setLooptijdMaanden] = useState(
    abonnement?.looptijdMaanden != null ? String(abonnement.looptijdMaanden) : "",
  );

  const [naamMissing, setNaamMissing] = useState(false);
  const [dagenMissing, setDagenMissing] = useState(false);
  const [prijsMissing, setPrijsMissing] = useState(false);
  const [notice, setNotice] = useState<SaveNotice>(null);
  const { mutate, isLoading: saving } = useCustomMutation();

  const submit = () => {
    const dagen = num(dagenPerWeek);
    const prijs = num(prijsPerMaand);
    setNaamMissing(naam.trim() === "");
    setDagenMissing(dagen == null);
    setPrijsMissing(prijs == null);
    if (naam.trim() === "" || dagen == null || prijs == null) return;
    setNotice(null);

    const values = {
      naam: naam.trim(),
      vestigingId: null,
      diersoortId: diersoortId === "" ? null : Number(diersoortId),
      dagenPerWeek: dagen,
      vasteDagenJson: null,
      prijsPerMaand: prijs,
      extraDagenStrategy,
      looptijdType,
      looptijdMaanden: looptijdType === "BEPAALDE_TIJD" ? num(looptijdMaanden) : null,
      // create defaults to CONCEPT (publiceren toggles to ACTIEF); keep an existing status on edit.
      status: abonnement?.status ?? "CONCEPT",
      actief: true,
      ...(abonnement ? { version: abonnement.version } : {}),
    };

    mutate(
      {
        url: abonnement ? `creche-tarief/abonnementen/${abonnement.id}` : "creche-tarief/abonnementen",
        method: abonnement ? "put" : "post",
        values,
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => onSaved(),
        onError: (err) => setNotice(statusToErr(err)),
      },
    );
  };

  return (
    <Modal
      open
      title={abonnement ? t("crecheTarief.abonnement.dialog.edit.title") : t("crecheTarief.abonnement.dialog.new.title")}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button onClick={submit} disabled={saving}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <div className="asiel-tarief__form">
        {notice && (
          <div className="asiel-tarief__notice asiel-tarief__notice--error" role="alert">
            {t(`crecheTarief.notice.${notice}`)}
          </div>
        )}
        <Field
          label={t("crecheTarief.abonnement.field.naam")}
          value={naam}
          onChange={(e) => setNaam(e.target.value)}
        />
        {naamMissing && (
          <div className="asiel-tarief__field-error">{t("crecheTarief.abonnement.field.naam.required")}</div>
        )}
        <Field
          label={t("crecheTarief.field.diersoortId")}
          value={diersoortId}
          inputMode="numeric"
          onChange={(e) => setDiersoortId(e.target.value)}
        />
        <Field
          label={t("crecheTarief.abonnement.field.dagenPerWeek")}
          value={dagenPerWeek}
          inputMode="numeric"
          onChange={(e) => setDagenPerWeek(e.target.value)}
        />
        {dagenMissing && (
          <div className="asiel-tarief__field-error">
            {t("crecheTarief.abonnement.field.dagenPerWeek.required")}
          </div>
        )}
        <Field
          label={t("crecheTarief.abonnement.field.prijsPerMaand")}
          value={prijsPerMaand}
          inputMode="decimal"
          onChange={(e) => setPrijsPerMaand(e.target.value)}
        />
        {prijsMissing && (
          <div className="asiel-tarief__field-error">
            {t("crecheTarief.abonnement.field.prijsPerMaand.required")}
          </div>
        )}
        <SelectField
          label={t("crecheTarief.abonnement.field.strategy")}
          value={extraDagenStrategy}
          onChange={(e) => setExtraDagenStrategy(e.target.value)}
          options={EXTRA_DAGEN_STRATEGIES.map((s) => ({
            value: s,
            label: t(`crecheTarief.abonnement.strategy.${s}`),
          }))}
        />
        <SelectField
          label={t("crecheTarief.abonnement.field.looptijd")}
          value={looptijdType}
          onChange={(e) => setLooptijdType(e.target.value)}
          options={LOOPTIJD_TYPES.map((l) => ({
            value: l,
            label: t(`crecheTarief.abonnement.looptijd.${l}`),
          }))}
        />
        {looptijdType === "BEPAALDE_TIJD" && (
          <Field
            label={t("crecheTarief.abonnement.field.looptijdMaanden")}
            value={looptijdMaanden}
            inputMode="numeric"
            onChange={(e) => setLooptijdMaanden(e.target.value)}
          />
        )}
      </div>
    </Modal>
  );
}

// ── Pakketsaldo lookup panel (Story 4.4, read-only customer-card) ─────────────────────────────────
function PakketsaldoPanel() {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";

  const [klantId, setKlantId] = useState("");
  const [query, setKlantQuery] = useState<number | null>(null);

  const { data, isLoading, isError } = useCustom<Pakketsaldo[]>({
    url: "creche-tarief/pakketsaldo",
    method: "get",
    config: { query: { klantId: query } },
    queryOptions: { enabled: query != null, retry: false },
  });
  const rows = useMemo(() => (Array.isArray(data?.data) ? data!.data : []), [data]);

  const columns: Column<Pakketsaldo>[] = [
    { header: t("crecheTarief.pakketsaldo.col.dier"), cell: (r) => r.dierNaam },
    { header: t("crecheTarief.pakketsaldo.col.rittenkaart"), cell: (r) => r.rittenkaartNaam ?? "—" },
    {
      header: t("crecheTarief.pakketsaldo.col.rittenOver"),
      align: "right",
      cell: (r) =>
        r.rittenOver != null
          ? `${r.rittenOver}${r.rittenTotaal != null ? ` / ${r.rittenTotaal}` : ""}`
          : "—",
    },
    {
      header: t("crecheTarief.pakketsaldo.col.verloop"),
      cell: (r) => (r.verloopDatum ? formatDate(r.verloopDatum, locale) : "—"),
    },
    { header: t("crecheTarief.pakketsaldo.col.abonnement"), cell: (r) => r.abonnementNaam ?? "—" },
  ];

  return (
    <fieldset className="asiel-tarief__section">
      <legend>{t("crecheTarief.pakketsaldo.title")}</legend>
      <p className="asiel-tarief__cond-empty">{t("crecheTarief.pakketsaldo.hint")}</p>
      <div className="pension-tarief__plan-row">
        <Field
          label={t("crecheTarief.pakketsaldo.field.klantId")}
          value={klantId}
          inputMode="numeric"
          onChange={(e) => setKlantId(e.target.value)}
        />
        <Button variant="secondary" onClick={() => setKlantQuery(num(klantId) != null ? Number(num(klantId)) : null)}>
          {t("crecheTarief.pakketsaldo.lookup")}
        </Button>
      </div>
      {query != null &&
        (isError ? (
          <div className="asiel-tarief__notice asiel-tarief__notice--error">
            {t("crecheTarief.pakketsaldo.load.error")}
          </div>
        ) : (
          <IndeelbareTabel tabelKey="creche-tarief-overview-4"
            columns={columns}
            rows={rows}
            getRowKey={(r) => String(r.saldoId)}
            empty={isLoading ? t("common.loading") : t("crecheTarief.pakketsaldo.empty")}
          />
        ))}
    </fieldset>
  );
}
